import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { AppBar } from '@/components/ui/AppBar';
import { ErrorState } from '@/components/ui/ErrorState';
import { Icon } from '@/components/icons/Icon';
import { Markdown } from '@/components/ui/Markdown';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import { formatDate } from '@/lib/prose';
import { sectorParam, sectorPath, useSector } from '@/lib/sector';

/**
 * One article.
 *
 * ── THE MEASURE IS THE DESIGN ───────────────────────────────────────────────
 *
 * 68 characters a line, which is the band long-form text has been set in since
 * long before screens. Wider and the eye loses its place returning to the left
 * edge; much narrower and every sentence breaks three times. The whole page is
 * built around holding that measure at every width, which is why the body
 * column does not stretch to the viewport on a desktop.
 *
 * ── AND THE TITLE REACHES THE BROWSER TAB ───────────────────────────────────
 *
 * Set here rather than server-side because this is a single-page app: without
 * it, every article shares one tab title and a reader with four of them open
 * cannot tell them apart. It is restored on unmount — a stale article title
 * left on the checkout screen would be worse than the default.
 *
 * This is NOT social-preview metadata. A crawler does not run JavaScript, so
 * `seo` in the payload is for whatever renders the share card server-side; it
 * is carried in the response rather than invented here so there is one source
 * for it when that lands.
 */
export function ArticleScreen() {
  const { slug } = useParams<{ slug: string }>();
  const sector = useSector();
  const path = (to: string) => sectorPath(sector, to);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['blog', 'article', sector, slug],
    queryFn: ({ signal }) => api.blogArticle(slug!, sectorParam(sector), signal),
    enabled: Boolean(slug),
  });

  useEffect(() => {
    if (!data?.title) return;

    const previous = document.title;
    document.title = `${data.title} · ChiCare`;

    return () => {
      document.title = previous;
    };
  }, [data?.title]);

  if (isError) {
    return (
      <>
        <AppBar back title="Article" />
        <ErrorState
          title="We couldn't find that article"
          body="The link may be old, or it may not be published yet."
          action={
            <Link
              to={path('/blog')}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-btn bg-blue px-4 text-[13px] font-semibold text-white no-underline"
            >
              <Icon name="back" size={14} />
              All articles
            </Link>
          }
        />
      </>
    );
  }

  return (
    <>
      <AppBar back title={data?.title ?? ' '} />

      <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-6">
        {isLoading || !data ? (
          <div className="mx-auto max-w-[68ch]">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="mt-3 h-9 w-full" />
            <Skeleton className="mt-2 h-9 w-2/3" />
            <Skeleton className="mt-5 aspect-[16/9] w-full rounded-card" />
            <div className="mt-6 space-y-5">
              <SkeletonText lines={4} />
              <SkeletonText lines={5} />
              <SkeletonText lines={3} />
            </div>
          </div>
        ) : (
          <>
            <article className="mx-auto max-w-[68ch]">
              <nav className="mb-4 text-[12px]">
                <Link
                  to={path('/blog')}
                  className="inline-flex min-h-9 items-center gap-1.5 font-medium text-blue no-underline"
                >
                  <Icon name="back" size={14} />
                  Blog
                </Link>
              </nav>

              <header>
                {data.category ? (
                  <p className="m-0 text-[11px] font-bold uppercase tracking-[0.07em] text-blue">
                    {data.category}
                  </p>
                ) : null}

                <h1 className="m-0 mt-1.5 font-display text-[28px] font-bold leading-[1.15] tracking-[-0.03em] text-ink sm:text-[38px]">
                  {data.title}
                </h1>

                {data.excerpt ? (
                  <p className="mt-3 text-[16px] leading-relaxed text-muted sm:text-[17.5px]">
                    {data.excerpt}
                  </p>
                ) : null}

                {/* Byline, date and reading time on one line. The byline is the
                    author of record, not whoever saved last — a sub-editor
                    fixing a typo does not take the credit or the blame. */}
                <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-line pt-4 text-[12px] text-faint">
                  <span className="font-semibold text-muted">{data.author}</span>
                  {data.published_at ? <span>· {formatDate(data.published_at)}</span> : null}
                  {data.reading_minutes ? (
                    <span className="flex items-center gap-1">
                      · <Icon name="clock" size={12} /> {data.reading_minutes} min read
                    </span>
                  ) : null}
                </div>
              </header>

              {data.cover?.url ? (
                <img
                  src={data.cover.url}
                  alt={data.cover.alt ?? ''}
                  className="mt-6 w-full rounded-card border border-line"
                />
              ) : null}

              <Markdown
                source={data.body ?? ''}
                variant="article"
                className="mt-7"
              />

              {/* ── The standing note ──────────────────────────────────────
                  An article on a pharmacy is read as advice whether or not it
                  was written as any, so it says plainly what it is. On the
                  veterinary side it says so in the sharper wording the sector
                  uses everywhere else. */}
              <aside className="mt-10 rounded-card border border-line bg-shell p-4">
                <p className="m-0 flex gap-2.5 text-[12.5px] leading-relaxed text-muted">
                  <Icon name="alert" size={16} className="mt-px shrink-0 text-blue" />
                  <span>
                    {sector === 'veterinary'
                      ? 'This is general information about animal health, not veterinary advice for your animals. Dose from the pack and ask your vet.'
                      : 'This is general health information, not a diagnosis or a prescription. For anything about your own treatment, speak to a pharmacist or a doctor.'}
                  </span>
                </p>
              </aside>

              {data.updated_at && data.updated_at !== data.published_at ? (
                <p className="mt-4 font-mono text-[11px] text-faint">
                  Updated {formatDate(data.updated_at)}
                </p>
              ) : null}
            </article>

            {data.related.length > 0 ? (
              <section className="mt-12 border-t border-line pt-8" data-testid="blog-related">
                <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em] text-ink">
                  More to read
                </h2>

                <div className="mt-4 grid gap-x-5 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
                  {data.related.map((post) => (
                    <Link
                      key={post.slug}
                      to={path(`/blog/${post.slug}`)}
                      className="group block no-underline"
                    >
                      {post.cover?.url ? (
                        <img
                          src={post.cover.url}
                          alt=""
                          loading="lazy"
                          className="aspect-[16/10] w-full rounded-card border border-line object-cover"
                        />
                      ) : (
                        <div
                          aria-hidden
                          className="grid aspect-[16/10] w-full place-items-center rounded-card border border-line bg-blue-tint"
                        >
                          <span className="font-display text-[30px] font-extrabold text-blue/35">
                            {post.title.trim().charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}

                      <h3 className="m-0 mt-2.5 font-display text-[14.5px] font-bold leading-snug text-ink group-hover:text-blue">
                        {post.title}
                      </h3>
                      {post.reading_minutes ? (
                        <p className="m-0 mt-1 text-[11px] text-faint">
                          {post.reading_minutes} min read
                        </p>
                      ) : null}
                    </Link>
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
